/**
 * Ask's figures for the reader's language (Intl): a table's cells by the format the answer gave
 * each column (share: 0.634 as 63,4 %), and a step's one-row figures, a share told by its
 * column's name. Only how a number is written changes, never the number.
 */
import type { AskCell, AskColumnFormat, AskStep } from './askApi'

const LOCALES: Record<string, string> = { de: 'de-AT', et: 'et-EE', en: 'en-GB', ru: 'ru-RU' }
const SHARE_NAME = /(share|rate|through|ratio|pct|level|covered|chance|osakaal|labimuuk)/i

export function localeOf(language: string | undefined): string {
    return LOCALES[(language ?? '').slice(0, 2)] ?? 'en-GB'
}

export function makeAskFormats(language: string | undefined) {
    const locale = localeOf(language)
    const share = new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 1 })
    const percent = new Intl.NumberFormat(locale, { maximumFractionDigits: 1 })
    const whole = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 })
    const one = new Intl.NumberFormat(locale, { maximumFractionDigits: 1 })
    const two = new Intl.NumberFormat(locale, { maximumFractionDigits: 2 })

    /** A plain number: whole from 1,000 up or when it is one; a decimal or two below. */
    function number(v: number): string {
        const a = Math.abs(v)
        if (Number.isInteger(v) || a >= 1000) return whole.format(v)
        return a >= 10 ? one.format(v) : two.format(v)
    }

    /** A cell as text (a visual one as its parts: 19 → 24, 9,7 (6–14), 2/7). */
    function cell(v: AskCell | undefined, format: AskColumnFormat): string {
        if (v === null || v === undefined || v === '') return '–'
        if (typeof v === 'object') {
            if ('from' in v) return `${cell(v.from, null)} → ${cell(v.to, null)}`
            if ('of' in v) return `${cell(v.value, null)}/${cell(v.of, null)}`
            return `${cell(v.value, null)} (${cell(v.low, null)}–${cell(v.high, null)})`
        }
        if (typeof v === 'boolean') return v ? '✓' : '–'
        if (typeof v !== 'number') return String(v)
        if (format === 'share') return share.format(v)
        if (format === 'percent') return `${percent.format(v)} %`
        if (format === 'change') return v > 0 ? `+${number(v)}` : v < 0 ? `−${number(-v)}` : number(v)
        return number(v)
    }

    /** A step's figures: a one-row result's numbers, a share by its column's name. */
    function figures(step: AskStep): string | null {
        const fs = (step.figures ?? []).slice(0, 2)
        if (!fs.length) return null
        return fs.map((f) => (SHARE_NAME.test(f.name) && Math.abs(f.value) <= 1.5 && !Number.isInteger(f.value) ? share.format(f.value) : number(f.value))).join(' · ')
    }

    /** An estimate: one decimal at most (a model's 9.7043 as 9,7). */
    function estimate(v: AskCell | undefined): string {
        return typeof v === 'number' ? one.format(v) : cell(v, null)
    }

    return { cell, estimate, figures, number, isNumeric: (format: AskColumnFormat) => format === 'number' || format === 'share' || format === 'percent' || format === 'change' || format === 'from_to' }
}
